/* eslint-disable max-lines */
import { IconNames } from '@blueprintjs/icons';
import {
    BenefitTypeOptions,
    BenefitTypes,
    NetworkStatuses,
    RouteCodeOptions,
} from '@strata/constants';
import { DateFormats, formatDate, sortAsc } from '@strata/helper-functions';
import { ActionButtonText } from '@strata/react-buttons';
import { CheckboxListFormik } from '@strata/react-checkbox-list';
import { DateInputFormik } from '@strata/react-date-time';
import { FormContainer } from '@strata/react-form-container';
import { FormInputContainer } from '@strata/react-form-input-container';
import { DollarInputFormik, TextAreaFormik, TextInputFormik } from '@strata/react-inputs';
import { RadioButtonGroupFormik } from '@strata/react-radio-button-group';
import { SuggestMultiSelectFormik, SuggestSingleSelectFormik } from '@strata/react-selects';
import { Icon } from '@strata/react-status-icons';
import { SuccessToast } from '@strata/react-toast-message';
import { validationStrings } from '@strata/strata-ui-strings';
import dayjs from 'dayjs';
import { FieldArray, Formik, useFormikContext } from 'formik';
import PropTypes from 'prop-types';
import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router';
import { v4 as uuidv4 } from 'uuid';
import * as Yup from 'yup';

import { AuditHistory } from 'components/AuditHistory';
import { FormPage } from 'components/FormPage';
import { ListPageHeader } from 'components/ListPageHeader';
import { CREATE_ROUTE, MAX_STANDARD_SKU_ID } from 'services/constants';
import { handleError } from 'services/ErrorManagement';
import { post, put, useGet } from 'services/Http';
import { ListSchema, ListType, useListTypeOptions } from 'services/ListManagementHelpers';
import { mapSuppliersToOptions } from 'services/NPISupport';
import { mapPayerSegment } from 'services/PayerSegmentSupport';

/** Const used to indicate condition set applies to all segments */
const ALL_SEGMENTS = 'N/A';
const MAX_DATE = '12/31/2999';

const ConditionKeys = {
    SEGMENT: 'segment_id',
    BENEFIT: 'benefit',
    ROUTE_CODE: 'assignment_route_code',
    ROUTE_RULE: 'route_rule_id',
    ROUTING_SCENARIO: 'routing_scenario',
    POST_LIVE_ASSIGNMENT: 'route_is_post_live',
    CONTRACT_RATE: 'rate_is_contract',
    PRODUCT: 'product_service_oid',
    PRODUCT_LIST: 'product_list_id',
    SUPPLIER: 'parent_pharmacy_npi',
    SUPPLIER_LIST: 'supplier_list_id',
};

const ConditionKeyNameMap = {
    [ConditionKeys.SEGMENT]: 'Payer segment',
    [ConditionKeys.BENEFIT]: 'Benefit type',
    [ConditionKeys.ROUTE_CODE]: 'Assignment route codes',
    [ConditionKeys.ROUTE_RULE]: 'Route rules',
    [ConditionKeys.ROUTING_SCENARIO]: 'Routing scenario',
    [ConditionKeys.POST_LIVE_ASSIGNMENT]: 'Post Live Referral Assignment',
    [ConditionKeys.CONTRACT_RATE]: 'Rate is contract',
    [ConditionKeys.PRODUCT]: 'Product',
    [ConditionKeys.PRODUCT_LIST]: 'Product list',
    [ConditionKeys.SUPPLIER]: 'Supplier',
    [ConditionKeys.SUPPLIER_LIST]: 'Supplier list',
};
const ConditionOptions = Object.entries(ConditionKeyNameMap).map(([value, name]) => ({
    value,
    name,
}));

const MultiSelectConditionKeys = [
    ConditionKeys.SEGMENT,
    ConditionKeys.BENEFIT,
    ConditionKeys.ROUTE_CODE,
    ConditionKeys.ROUTE_RULE,
];

const unpackCondition = (condition, conditionKeyOptions) =>
    Object.values(ConditionKeys)
        .filter(
            (key) =>
                (!Array.isArray(condition?.[key]) && condition?.[key] != null) ||
                (Array.isArray(condition?.[key]) && condition?.[key].length > 0),
        )
        .map((key) => ({
            condition_id: uuidv4(),
            key,
            value: (() => {
                const value = condition?.[key];

                // Handle segment_id condition specially since value needs to be list of strings instead of objects for checkbox list
                if (key === ConditionKeys.SEGMENT) {
                    // If segment condition is [N/A], treat as all segments
                    return value?.[0] === ALL_SEGMENTS
                        ? conditionKeyOptions[key]?.map((option) => option.value)
                        : value;
                }

                // Route rule options are loaded async based on other form selections, so just return value
                if (key === ConditionKeys.ROUTE_RULE) {
                    return value;
                }

                // Product needs to additionally match on is_brand field
                if (key === ConditionKeys.PRODUCT) {
                    return conditionKeyOptions[key]?.find(
                        (option) =>
                            value === option.value && condition?.is_brand === option.isBrand,
                    );
                }

                return Array.isArray(value)
                    ? conditionKeyOptions[key]?.filter((option) => value?.includes(option.value))
                    : conditionKeyOptions[key]?.find((option) => value === option.value);
            })(),
        }));

const packCondition = (conditions, conditionKeyOptions) => {
    const condition = {};
    conditions.forEach(({ key, value }) => {
        // Handle segment_id condition specially since value is list of strings instead of objects
        if (key === ConditionKeys.SEGMENT) {
            // If segment condition has all segments, return [N/A]
            condition[key] =
                value.length === conditionKeyOptions[key]?.length ? [ALL_SEGMENTS] : value;
        }
        // Route rule options are stored as an array of ids so just return value
        else if (key === ConditionKeys.ROUTE_RULE) {
            condition[key] = value;
        }
        // Extract product oid and is_brand for product condition
        else if (key === ConditionKeys.PRODUCT) {
            condition[key] = value.value;
            condition.is_brand = value.isBrand;
        } else {
            condition[key] = Array.isArray(value)
                ? value.map((option) => option.value)
                : value?.value;
        }
    });
    return condition;
};

const mapProductsToBrandAndGenericProducts = (configuredProductsList) => {
    const products = [];
    configuredProductsList?.forEach((product) => {
        products.push({
            value: product.oid,
            productServiceOid: product.oid,
            name: product.brand_product_description,
            secondaryData: 'Brand',
            isBrand: true,
        });

        if (
            product.generic_product_description &&
            product.brand_product_description.toLowerCase() !==
                product.generic_product_description?.toLowerCase()
        ) {
            products.push({
                value: product.oid,
                productServiceOid: product.oid,
                name: product.generic_product_description,
                secondaryData: 'Generic',
                isBrand: false,
            });
        }
    });
    // Filter out duplicate names when multiple policies are configured
    return [...new Map(products.map((product) => [product.name, product])).values()].sort((a, b) =>
        sortAsc(a.name, b.name),
    );
};

/** Special multi-select component that loads in the route rules for the first selected segment id & benefit */
const RouteRuleSelector = ({
    id,
    disabled,
    payerSegmentOptions = undefined,
    condition = undefined,
    segmentId = undefined,
    benefit = undefined,
}) => {
    const formik = useFormikContext();
    const payerId = payerSegmentOptions?.find(({ value }) => value === segmentId)?.payer_id;
    const routeRules = useGet(
        payerId && segmentId && benefit
            ? `/clients/admin/${payerId}/${segmentId}/route_rules/${benefit}`
            : null,
    );
    const routeRuleOptions = useMemo(
        () => routeRules?.map((route) => ({ name: route.route_name, value: route.route_rule_id })),
        [routeRules],
    );
    return (
        <SuggestMultiSelectFormik
            id={id}
            required
            disabled={disabled}
            values={routeRuleOptions}
            isLoading={!routeRuleOptions}
            selectedItems={routeRuleOptions?.filter((option) =>
                condition.value?.includes(option.value),
            )}
            onChange={(newValues) =>
                formik.setFieldValue(
                    id,
                    newValues.map((v) => v.value),
                )
            }
        />
    );
};
RouteRuleSelector.propTypes = {
    id: PropTypes.string.isRequired,
    disabled: PropTypes.bool.isRequired,
    payerSegmentOptions: PropTypes.arrayOf(
        PropTypes.shape({
            value: PropTypes.string.isRequired,
            payer_id: PropTypes.string.isRequired,
        }),
    ),
    condition: PropTypes.shape({ value: PropTypes.arrayOf(PropTypes.string) }),
    segmentId: PropTypes.string,
    benefit: PropTypes.oneOf(Object.values(BenefitTypes)),
};

// eslint-disable-next-line complexity
export const ProgramRuleDetails = () => {
    const { pharmacySkuId } = useParams();
    const navigate = useNavigate();

    const goHome = () => navigate('/program-management/program-rules');

    const isCreate = pharmacySkuId === CREATE_ROUTE;
    const isStandardSku = !isCreate && pharmacySkuId <= MAX_STANDARD_SKU_ID;

    const programRules = useGet(isCreate ? '/finance/skus' : null);
    const programRule = useGet(!isCreate ? `/finance/skus/${pharmacySkuId}` : null);
    const auditHistoryData = useGet(!isCreate ? `/finance/skus/${pharmacySkuId}/audit` : null);

    const isLoading = (isCreate && !programRules) || (!isCreate && !programRule);

    // Load data for condition dropdowns
    const payerSegments = useGet('/reference/payer_segments');
    const payerSegmentOptions = payerSegments
        ?.map(mapPayerSegment)
        .sort(({ name: a }, { name: b }) => sortAsc(a, b));

    const suppliers = useGet('/reference/suppliers/parents');
    const supplierOptions = mapSuppliersToOptions(suppliers)?.filter(
        (supplier) => supplier.network_status?.trim() !== NetworkStatuses.INACTIVE,
    );

    const products = useGet('/reference/product_benefit_configs/');
    const productOptions = mapProductsToBrandAndGenericProducts(products);

    const productListOptions = useListTypeOptions(ListSchema.finance, ListType.product);
    const supplierListOptions = useListTypeOptions(ListSchema.finance, ListType.supplier);

    /** Map with value options for each condition key */
    const conditionKeyOptions = {
        [ConditionKeys.SEGMENT]: payerSegmentOptions,
        [ConditionKeys.BENEFIT]: BenefitTypeOptions,
        [ConditionKeys.ROUTE_CODE]: RouteCodeOptions.map((option) => ({
            ...option,
            // Remove secondaryData for brevity in dropdown
            secondaryData: undefined,
        })),
        [ConditionKeys.ROUTING_SCENARIO]: [
            { name: 'Core', value: 'Core' },
            { name: 'Custom', value: 'Custom' },
        ],
        [ConditionKeys.POST_LIVE_ASSIGNMENT]: [
            { name: ConditionKeyNameMap[ConditionKeys.POST_LIVE_ASSIGNMENT], value: true },
        ],
        [ConditionKeys.CONTRACT_RATE]: [
            { name: ConditionKeyNameMap[ConditionKeys.CONTRACT_RATE], value: true },
        ],
        [ConditionKeys.PRODUCT]: productOptions,
        [ConditionKeys.PRODUCT_LIST]: productListOptions,
        [ConditionKeys.SUPPLIER]: supplierOptions,
        [ConditionKeys.SUPPLIER_LIST]: supplierListOptions,
    };

    const initialStartDate = programRule?.sku_start_date ?? dayjs().toISOString();
    const initialEndDate = programRule?.sku_end_date ?? dayjs(MAX_DATE).toISOString();
    return (
        <Formik
            initialValues={{
                pharmacy_sku_id: !isCreate
                    ? programRule?.pharmacy_sku_id
                    : Math.max(...(programRules ?? []).map((rule) => rule.pharmacy_sku_id)) + 1,
                program_name: programRule?.program_name,
                program_description: programRule?.program_description,
                program_status: programRule?.program_status ?? true,
                sku_start_date: initialStartDate,
                sku_end_date: initialEndDate,
                period_configs: programRule?.period_configs?.map((config) => ({
                    ...config,
                    conditions: unpackCondition(config.condition, conditionKeyOptions),
                })) ?? [
                    {
                        pharmacy_sku_period_id: uuidv4(),
                        is_new: true,
                        conditions: [
                            {
                                condition_id: uuidv4(),
                                key: ConditionKeys.SEGMENT,
                            },
                            {
                                condition_id: uuidv4(),
                                key: ConditionKeys.BENEFIT,
                            },
                        ],
                    },
                ],
            }}
            enableReinitialize
            validationSchema={Yup.object({
                program_name: Yup.string()
                    .required(validationStrings.requiredField.errorMessage)
                    .max(100, validationStrings.maxCharacter[100]),
                program_description: Yup.string()
                    .required(validationStrings.requiredField.errorMessage)
                    .max(1000, validationStrings.maxCharacter.lambda(1000)),
                program_status: Yup.bool().required(validationStrings.requiredField.errorMessage),
                sku_start_date: DateInputFormik.validator()
                    .required(validationStrings.requiredField.errorMessage)
                    .min(
                        !dayjs(initialStartDate).isBefore(dayjs(), 'day')
                            ? dayjs().startOf('day').toDate()
                            : '2000-01-01',
                        'Start date cannot be in the past',
                    ),
                sku_end_date: DateInputFormik.validator()
                    .required(validationStrings.requiredField.errorMessage)
                    .max(dayjs(MAX_DATE).toDate(), 'Max date is 12/31/2999'),
                period_configs: Yup.array(
                    Yup.object({
                        pharmacy_sku_period_id: Yup.string().required(
                            validationStrings.requiredField.errorMessage,
                        ),
                        sku_start_date_override: DateInputFormik.validator().nullable(),
                        sku_end_date_override: DateInputFormik.validator().nullable(),
                        sku_fee: Yup.number()
                            .min(0, validationStrings.number.positiveNumbersOnly)
                            .required(validationStrings.requiredField.errorMessage),
                        conditions: Yup.array(
                            Yup.object({
                                key: Yup.string().required(
                                    validationStrings.requiredField.errorMessage,
                                ),
                                value: Yup.mixed().when('key', {
                                    is: (key) => MultiSelectConditionKeys.includes(key),
                                    then: () =>
                                        Yup.array()
                                            .required(validationStrings.requiredField.errorMessage)
                                            .min(1, validationStrings.requiredField.errorMessage),
                                    otherwise: () =>
                                        Yup.object().required(
                                            validationStrings.requiredField.errorMessage,
                                        ),
                                }),
                            }).required(validationStrings.requiredField.errorMessage),
                        )
                            .required(validationStrings.requiredField.errorMessage)
                            .min(1, validationStrings.requiredField.errorMessage),
                    }).required(validationStrings.requiredField.errorMessage),
                )
                    .required(validationStrings.requiredField.errorMessage)
                    .min(1, validationStrings.requiredField.errorMessage),
            })}
            onSubmit={async (values) => {
                try {
                    const body = {
                        pharmacy_sku_id: values.pharmacy_sku_id,
                        program_name: values.program_name,
                        program_description: values.program_description,
                        program_status: values.program_status,
                        sku_start_date: formatDate(values.sku_start_date, DateFormats.HYPHEN_DATE),
                        sku_end_date: formatDate(values.sku_end_date, DateFormats.HYPHEN_DATE),
                        period_configs: values.period_configs.map((config) => ({
                            pharmacy_sku_id: values.pharmacy_sku_id,
                            pharmacy_sku_period_id: !config.is_new
                                ? config.pharmacy_sku_period_id
                                : undefined,
                            sku_fee: config.sku_fee,
                            sku_start_date_override: formatDate(
                                config.sku_start_date_override,
                                DateFormats.HYPHEN_DATE,
                            ),
                            sku_end_date_override: formatDate(
                                config.sku_end_date_override,
                                DateFormats.HYPHEN_DATE,
                            ),
                            condition: packCondition(config.conditions, conditionKeyOptions),
                        })),
                    };

                    if (isCreate) {
                        await post('/finance/skus', body);
                    } else {
                        await put(`/finance/skus/${pharmacySkuId}`, body);
                    }

                    await SuccessToast({ message: 'Program saved successfully.' });
                    goHome();
                } catch (err) {
                    handleError('Error saving program.', err);
                }
            }}
        >
            {(formik) => (
                <FormPage
                    columns={[
                        <FormContainer
                            key="form"
                            formTitle={`${isCreate ? 'Create' : 'Edit'} Program`}
                            isLoading={isLoading}
                            onFormSubmit={formik.handleSubmit}
                            isSubmitting={formik.isSubmitting || !(formik.dirty && formik.isValid)}
                            onSecondaryButtonClick={goHome}
                            formLeftContent={
                                <>
                                    <TextInputFormik
                                        id="program_name"
                                        label="Program Name"
                                        required
                                        disabled={isStandardSku}
                                    />
                                    <TextAreaFormik
                                        id="program_description"
                                        label="Description"
                                        required
                                        disabled={isStandardSku}
                                    />
                                    <RadioButtonGroupFormik
                                        name="program_status"
                                        label="Status"
                                        required
                                        disabled={isStandardSku}
                                        options={['Active', 'Inactive']}
                                        selectedValue={
                                            formik.values.program_status ? 'Active' : 'Inactive'
                                        }
                                        onChange={(newValue) =>
                                            formik.setFieldValue(
                                                'program_status',
                                                newValue === 'Active',
                                            )
                                        }
                                    />
                                    <div className="sds_form-page__input-group">
                                        <DateInputFormik
                                            id="sku_start_date"
                                            label="Program Start Date"
                                            required
                                            // Min allowed date is today if creating new rule or editing existing rule with start date in future
                                            // Otherwise allow past start dates for existing rules with start date in past
                                            minDate={
                                                !dayjs(
                                                    formik.initialValues.sku_start_date,
                                                ).isBefore(dayjs(), 'day')
                                                    ? dayjs().startOf('day').toDate()
                                                    : undefined
                                            }
                                            // Disable when program is inactive or start date has passed
                                            disabled={
                                                isStandardSku ||
                                                !formik.values.program_status ||
                                                dayjs(formik.initialValues.sku_start_date).isBefore(
                                                    dayjs(),
                                                    'day',
                                                )
                                            }
                                        />
                                        <DateInputFormik
                                            id="sku_end_date"
                                            label="Program End Date"
                                            required
                                            disabled={
                                                isStandardSku || !formik.values.program_status
                                            }
                                            minDate={dayjs(formik.values.sku_start_date)
                                                .startOf('day')
                                                .add(1, 'day')
                                                .toDate()}
                                            maxDate={dayjs(MAX_DATE).toDate()}
                                        />
                                    </div>
                                    <ListPageHeader pageTitle="Program Conditions" />
                                    <div
                                        className="flex__gap--medium"
                                        style={{ flexDirection: 'column' }}
                                    >
                                        <FieldArray name="period_configs">
                                            {(periodConfigArrayHelpers) => (
                                                <>
                                                    {formik.values[
                                                        periodConfigArrayHelpers.name
                                                    ].map((config, i) => (
                                                        <FormInputContainer
                                                            key={config.pharmacy_sku_period_id}
                                                        >
                                                            <FieldArray
                                                                name={`${periodConfigArrayHelpers.name}.${i}.conditions`}
                                                            >
                                                                {(conditionArrayHelpers) => (
                                                                    <>
                                                                        {!isStandardSku &&
                                                                            formik.values
                                                                                .program_status &&
                                                                            !dayjs().isAfter(
                                                                                dayjs(
                                                                                    formik
                                                                                        .initialValues[
                                                                                        periodConfigArrayHelpers
                                                                                            .name
                                                                                    ][i]
                                                                                        ?.sku_start_date_override,
                                                                                ),
                                                                                'day',
                                                                            ) && (
                                                                                <Icon
                                                                                    icon={
                                                                                        IconNames.TRASH
                                                                                    }
                                                                                    title="Remove Condition Set"
                                                                                    intent={
                                                                                        Icon.Intent
                                                                                            .SECONDARY
                                                                                    }
                                                                                    className="sds_program-rule__remove-condition-set-icon"
                                                                                    onClick={() =>
                                                                                        periodConfigArrayHelpers.remove(
                                                                                            i,
                                                                                        )
                                                                                    }
                                                                                />
                                                                            )}
                                                                        {config.conditions.map(
                                                                            (condition, j) => {
                                                                                const conditionRequired =
                                                                                    condition.key ===
                                                                                        ConditionKeys.SEGMENT ||
                                                                                    condition.key ===
                                                                                        ConditionKeys.BENEFIT;
                                                                                return (
                                                                                    <div
                                                                                        key={
                                                                                            condition.condition_id
                                                                                        }
                                                                                        className="sds_program-rule__row"
                                                                                    >
                                                                                        <div className="sds_program-rule__label sds_program-rule__label--primary">
                                                                                            {j === 0
                                                                                                ? 'WHEN'
                                                                                                : 'AND'}
                                                                                        </div>
                                                                                        <SuggestSingleSelectFormik
                                                                                            id={`${conditionArrayHelpers.name}.${j}.key`}
                                                                                            required
                                                                                            disabled={
                                                                                                isStandardSku ||
                                                                                                conditionRequired ||
                                                                                                !formik
                                                                                                    .values
                                                                                                    .program_status
                                                                                            }
                                                                                            values={ConditionOptions.filter(
                                                                                                ({
                                                                                                    value,
                                                                                                }) =>
                                                                                                    !config.conditions
                                                                                                        .map(
                                                                                                            ({
                                                                                                                key,
                                                                                                            }) =>
                                                                                                                key,
                                                                                                        )
                                                                                                        .includes(
                                                                                                            value,
                                                                                                        ),
                                                                                            )}
                                                                                            selectedItem={ConditionOptions.find(
                                                                                                ({
                                                                                                    value,
                                                                                                }) =>
                                                                                                    value ===
                                                                                                    condition.key,
                                                                                            )}
                                                                                            onChange={(
                                                                                                newVal,
                                                                                            ) =>
                                                                                                conditionArrayHelpers.replace(
                                                                                                    j,
                                                                                                    {
                                                                                                        ...condition,
                                                                                                        key: newVal?.value,
                                                                                                        value: undefined,
                                                                                                    },
                                                                                                )
                                                                                            }
                                                                                        />
                                                                                        <div className="sds_program-rule__label">
                                                                                            is
                                                                                        </div>
                                                                                        {(() => {
                                                                                            if (
                                                                                                condition.key ===
                                                                                                ConditionKeys.SEGMENT
                                                                                            ) {
                                                                                                return (
                                                                                                    <CheckboxListFormik
                                                                                                        name={`${conditionArrayHelpers.name}.${j}.value`}
                                                                                                        required
                                                                                                        showSelectAll
                                                                                                        disabled={
                                                                                                            isStandardSku ||
                                                                                                            !formik
                                                                                                                .values
                                                                                                                .program_status
                                                                                                        }
                                                                                                        options={
                                                                                                            conditionKeyOptions[
                                                                                                                condition
                                                                                                                    .key
                                                                                                            ] ??
                                                                                                            []
                                                                                                        }
                                                                                                    />
                                                                                                );
                                                                                            }
                                                                                            if (
                                                                                                condition.key ===
                                                                                                ConditionKeys.ROUTE_RULE
                                                                                            ) {
                                                                                                return (
                                                                                                    <RouteRuleSelector
                                                                                                        id={`${conditionArrayHelpers.name}.${j}.value`}
                                                                                                        disabled={
                                                                                                            isStandardSku ||
                                                                                                            !formik
                                                                                                                .values
                                                                                                                .program_status
                                                                                                        }
                                                                                                        payerSegmentOptions={
                                                                                                            payerSegmentOptions
                                                                                                        }
                                                                                                        condition={
                                                                                                            condition
                                                                                                        }
                                                                                                        segmentId={
                                                                                                            config.conditions?.find(
                                                                                                                (
                                                                                                                    c,
                                                                                                                ) =>
                                                                                                                    c.key ===
                                                                                                                    ConditionKeys.SEGMENT,
                                                                                                            )
                                                                                                                ?.value?.[0]
                                                                                                        }
                                                                                                        benefit={
                                                                                                            config.conditions?.find(
                                                                                                                (
                                                                                                                    c,
                                                                                                                ) =>
                                                                                                                    c.key ===
                                                                                                                    ConditionKeys.BENEFIT,
                                                                                                            )
                                                                                                                ?.value?.[0]
                                                                                                                ?.value
                                                                                                        }
                                                                                                    />
                                                                                                );
                                                                                            }

                                                                                            return MultiSelectConditionKeys.includes(
                                                                                                condition.key,
                                                                                            ) ? (
                                                                                                <SuggestMultiSelectFormik
                                                                                                    id={`${conditionArrayHelpers.name}.${j}.value`}
                                                                                                    required
                                                                                                    disabled={
                                                                                                        isStandardSku ||
                                                                                                        !formik
                                                                                                            .values
                                                                                                            .program_status
                                                                                                    }
                                                                                                    values={
                                                                                                        conditionKeyOptions[
                                                                                                            condition
                                                                                                                .key
                                                                                                        ]
                                                                                                    }
                                                                                                    isLoading={
                                                                                                        !conditionKeyOptions[
                                                                                                            condition
                                                                                                                .key
                                                                                                        ]
                                                                                                    }
                                                                                                />
                                                                                            ) : (
                                                                                                <SuggestSingleSelectFormik
                                                                                                    id={`${conditionArrayHelpers.name}.${j}.value`}
                                                                                                    required
                                                                                                    disabled={
                                                                                                        isStandardSku ||
                                                                                                        condition.key ===
                                                                                                            ConditionKeys.POST_LIVE_ASSIGNMENT ||
                                                                                                        condition.key ===
                                                                                                            ConditionKeys.CONTRACT_RATE ||
                                                                                                        !formik
                                                                                                            .values
                                                                                                            .program_status
                                                                                                    }
                                                                                                    values={
                                                                                                        conditionKeyOptions[
                                                                                                            condition
                                                                                                                .key
                                                                                                        ]
                                                                                                    }
                                                                                                    isLoading={
                                                                                                        !conditionKeyOptions[
                                                                                                            condition
                                                                                                                .key
                                                                                                        ]
                                                                                                    }
                                                                                                />
                                                                                            );
                                                                                        })()}
                                                                                        {!isStandardSku &&
                                                                                            !conditionRequired &&
                                                                                            formik
                                                                                                .values
                                                                                                .program_status && (
                                                                                                <Icon
                                                                                                    className="margin__top--small"
                                                                                                    icon={
                                                                                                        IconNames.CROSS
                                                                                                    }
                                                                                                    title="Remove Condition"
                                                                                                    onClick={() =>
                                                                                                        conditionArrayHelpers.remove(
                                                                                                            j,
                                                                                                        )
                                                                                                    }
                                                                                                />
                                                                                            )}
                                                                                    </div>
                                                                                );
                                                                            },
                                                                        )}
                                                                        {config.conditions.length <
                                                                            Object.values(
                                                                                ConditionKeys,
                                                                            ).length &&
                                                                            !isStandardSku &&
                                                                            formik.values
                                                                                .program_status && (
                                                                                <div className="sds_program-rule__row">
                                                                                    <ActionButtonText
                                                                                        className="margin__bottom--medium"
                                                                                        style={{
                                                                                            gridColumnStart: 2,
                                                                                        }}
                                                                                        leftIcon={
                                                                                            IconNames.ADD
                                                                                        }
                                                                                        label="Add Another Condition"
                                                                                        onClick={() =>
                                                                                            conditionArrayHelpers.push(
                                                                                                {
                                                                                                    condition_id:
                                                                                                        uuidv4(),
                                                                                                },
                                                                                            )
                                                                                        }
                                                                                    />
                                                                                </div>
                                                                            )}
                                                                    </>
                                                                )}
                                                            </FieldArray>
                                                            <div className="sds_program-rule__row">
                                                                <div className="sds_program-rule__label sds_program-rule__label--primary">
                                                                    FROM
                                                                </div>
                                                                <DateInputFormik
                                                                    id={`${periodConfigArrayHelpers.name}.${i}.sku_start_date_override`}
                                                                    // Disable when program is inactive or start date has passed
                                                                    disabled={
                                                                        isStandardSku ||
                                                                        !formik.values
                                                                            .program_status ||
                                                                        dayjs().isAfter(
                                                                            dayjs(
                                                                                formik
                                                                                    .initialValues[
                                                                                    periodConfigArrayHelpers
                                                                                        .name
                                                                                ][i]
                                                                                    ?.sku_start_date_override,
                                                                            ),
                                                                            'day',
                                                                        )
                                                                    }
                                                                    minDate={dayjs(
                                                                        formik.values
                                                                            .sku_start_date,
                                                                    )
                                                                        .startOf('day')
                                                                        .toDate()}
                                                                    maxDate={dayjs(
                                                                        formik.values.sku_end_date,
                                                                    )
                                                                        .endOf('day')
                                                                        .toDate()}
                                                                />
                                                                <div className="sds_program-rule__label">
                                                                    to
                                                                </div>
                                                                <DateInputFormik
                                                                    id={`${periodConfigArrayHelpers.name}.${i}.sku_end_date_override`}
                                                                    disabled={
                                                                        isStandardSku ||
                                                                        !formik.values
                                                                            .program_status
                                                                    }
                                                                    minDate={dayjs(
                                                                        config.sku_start_date_override,
                                                                    )
                                                                        .startOf('day')
                                                                        .add(1, 'day')
                                                                        .toDate()}
                                                                    maxDate={dayjs(
                                                                        formik.values.sku_end_date,
                                                                    )
                                                                        .endOf('day')
                                                                        .toDate()}
                                                                />
                                                            </div>
                                                            <div className="sds_program-rule__row">
                                                                <div className="sds_program-rule__label sds_program-rule__label--primary">
                                                                    FEE
                                                                </div>
                                                                <DollarInputFormik
                                                                    id={`${periodConfigArrayHelpers.name}.${i}.sku_fee`}
                                                                    required
                                                                    disabled={
                                                                        isStandardSku ||
                                                                        !formik.values
                                                                            .program_status
                                                                    }
                                                                />
                                                            </div>
                                                        </FormInputContainer>
                                                    ))}
                                                    {!isStandardSku &&
                                                        formik.values.program_status && (
                                                            <div>
                                                                <ActionButtonText
                                                                    leftIcon={IconNames.ADD}
                                                                    label="Add Another Condition Set"
                                                                    onClick={() =>
                                                                        periodConfigArrayHelpers.push(
                                                                            {
                                                                                pharmacy_sku_period_id:
                                                                                    uuidv4(),
                                                                                is_new: true,
                                                                                conditions: [
                                                                                    {
                                                                                        condition_id:
                                                                                            uuidv4(),
                                                                                        key: ConditionKeys.SEGMENT,
                                                                                    },
                                                                                    {
                                                                                        condition_id:
                                                                                            uuidv4(),
                                                                                        key: ConditionKeys.BENEFIT,
                                                                                    },
                                                                                ],
                                                                            },
                                                                        )
                                                                    }
                                                                />
                                                            </div>
                                                        )}
                                                </>
                                            )}
                                        </FieldArray>
                                        <FormInputContainer className="sds_program-rule__row">
                                            <div className="sds_program-rule__label">SKU</div>
                                            <div style={{ gridColumnStart: 2, gridColumnEnd: 5 }}>
                                                <TextInputFormik
                                                    id="pharmacy_sku_id"
                                                    required
                                                    disabled
                                                    value={formik.values.pharmacy_sku_id
                                                        ?.toString()
                                                        .padStart(4, '0')}
                                                />
                                            </div>
                                        </FormInputContainer>
                                    </div>
                                </>
                            }
                        />,
                        <AuditHistory key="audit" data={auditHistoryData} isCreate={isCreate} />,
                    ]}
                />
            )}
        </Formik>
    );
};
